/**
 * call-phase — pure state machine for the OPTIMISTIC call-start UI (FB34).
 *
 * Mirrors src/lib/rtc-recovery.ts's `(state, event) → { state, effects }`
 * shape (no sockets/timers/pc/React in here — every transition is
 * exhaustively unit-testable, see call-phase.test.ts). This machine answers
 * a different question than rtc-recovery.ts, though: rtc-recovery decides
 * whether a LIVE call recovers from a connection drop; this machine decides
 * what the call SURFACE renders before a call is live at all, and is the
 * fix for "tapping Call shows nothing for 2–5s" — the call screen mounts
 * the instant `START_TAPPED` fires, in a `connecting` phase, instead of
 * waiting for `isCalling` (see useCallMode.ts) to flip at the very end of a
 * multi-second async chain.
 *
 * `isCalling`/`isCallingRef` (useCallMode.ts) KEEP their existing internal
 * semantics unchanged — they still gate the audio pump, the mic-silence
 * watchdog, and rtc-recovery's arming, and still flip at the same point
 * they always have (once the OpenAI call answers, before ICE has actually
 * finished connecting). `callPhase` is a SEPARATE, purely external-facing
 * signal: every user-facing/external gate (the composer, TypingIndicator,
 * the mount of CallScreen itself, toggleCall's start-vs-stop branch) reads
 * THIS instead, because `isCalling` is not a safe axis during `connecting`
 * — it's still false while the call surface is already visible and "End"
 * must already work.
 *
 * States: idle → connecting → live → ending → idle, plus `failed` (a
 * terminal state, exited only by RETRY_TAPPED or END_TAPPED).
 *
 * Transport lives IN the state (not derived from context) because the two
 * transports define "live" differently:
 *   - ws (`?rtc=0`): there is no separate peer connection to bring up — the
 *     audio pump starts the instant capture is ready, so `live` = CAPTURE_READY.
 *   - rtc (the default transport): the call is only actually two-way once
 *     the peer connection has finished ICE negotiation AND the mic track has
 *     been attached to it — either one can lag the other (the mic can be
 *     slower under the FB34 latency-overlap path, see rtc-call.ts's
 *     `startRtcCallOffer`), so `live` = PC_CONNECTED AND TRACK_ATTACHED,
 *     gated independently (whichever arrives second flips it).
 *
 * `network` (a START_FAILED reason) means the peer connection reported
 * 'failed'/'closed' BEFORE this call ever reached `live` — i.e. the failure
 * the call-start UI most needs to show ("Couldn't reach Sara"), distinct
 * from a drop AFTER a call was already live (which stays rtc-recovery.ts's
 * job — that machine arms only once `callPhase` is `live`, so an initial-ICE
 * failure can never burn its one recall budget or show the mid-call
 * "hiccup" notice over the connecting treatment).
 */

// ── State ───────────────────────────────────────────────────────────

export type CallTransport = 'ws' | 'rtc';

export type CallPhase = 'idle' | 'connecting' | 'live' | 'ending' | 'failed';

export type CallFailureReason =
  | 'mic_denied'
  | 'timeout'
  | 'backend'
  | 'network'
  | 'ws_closed';

export interface CallPhaseState {
  phase: CallPhase;
  /** null only at `idle` (and again once TEARDOWN_DONE returns to idle) —
   *  every other phase carries the transport the current/last attempt used. */
  transport: CallTransport | null;
  /** Non-null only at `failed`. */
  reason: CallFailureReason | null;
  /** rtc-only bookkeeping, gating `live` independently per the file header —
   *  meaningless (and left false) for the ws transport, whose `live` gate is
   *  CAPTURE_READY alone. */
  rtcConnected: boolean;
  rtcTrackAttached: boolean;
}

export function initialCallPhaseState(): CallPhaseState {
  return {
    phase: 'idle',
    transport: null,
    reason: null,
    rtcConnected: false,
    rtcTrackAttached: false,
  };
}

// ── Events (inputs — driven by useCallMode.ts) ─────────────────────

export type CallPhaseEvent =
  /** User tapped Call (or the driver auto-starts one) — idle → connecting. */
  | { type: 'START_TAPPED'; transport: CallTransport }
  /** buildCapture resolved with a live mic track. ws: this alone → live.
   *  rtc: informational only — rtc's live gate is PC_CONNECTED+TRACK_ATTACHED. */
  | { type: 'CAPTURE_READY' }
  /** rtc: the SDP answer landed (a fresh OpenAI call now exists server-side).
   *  Informational bookkeeping only — does not itself flip `live`. */
  | { type: 'ANSWER_RECEIVED' }
  /** rtc: pc.connectionState reported 'connected'. */
  | { type: 'PC_CONNECTED' }
  /** rtc: the mic track has been attached to the peer connection (either via
   *  addTrack up front, or replaceTrack once buildCapture caught up under
   *  the latency-overlap path). */
  | { type: 'TRACK_ATTACHED' }
  /** The current connecting attempt failed outright — connecting → failed. */
  | { type: 'START_FAILED'; reason: CallFailureReason }
  /** The failed state's "Try again" chip — failed → connecting, reusing the
   *  same transport as the failed attempt. */
  | { type: 'RETRY_TAPPED' }
  /** User (or an external stop, e.g. a lost connection with no more
   *  retries) ended the call — {connecting, live, failed} → ending. */
  | { type: 'END_TAPPED' }
  /** The driver finished its synchronous teardown — ending → idle. */
  | { type: 'TEARDOWN_DONE' };

// ── Effects (outputs the driver executes) ──────────────────────────

/** The only side effect this machine asks for: a ~10s "is getUserMedia ever
 *  going to resolve?" watchdog (see useCallMode.ts's CONNECTING_WATCHDOG_MS)
 *  — getUserMedia has no timeout of its own, so an ignored permission prompt
 *  would otherwise leave `connecting` showing the same copy forever. Armed
 *  on every entry into `connecting` (fresh start or retry), cancelled on
 *  every exit (live, failed, or ending) — mirrors rtc-recovery.ts's
 *  START_GRACE_TIMER/CANCEL_GRACE_TIMER pairing exactly. */
export type CallPhaseEffect =
  | { type: 'START_CONNECTING_WATCHDOG' }
  | { type: 'CANCEL_CONNECTING_WATCHDOG' };

export interface CallPhaseResult {
  state: CallPhaseState;
  effects: CallPhaseEffect[];
}

function noop(state: CallPhaseState): CallPhaseResult {
  return { state, effects: [] };
}

/** Shared by START_TAPPED (from idle) and RETRY_TAPPED (from failed) — both
 *  land on a fresh `connecting` state and arm the same watchdog. */
function enterConnecting(transport: CallTransport): CallPhaseResult {
  return {
    state: {
      phase: 'connecting',
      transport,
      reason: null,
      rtcConnected: false,
      rtcTrackAttached: false,
    },
    effects: [{ type: 'START_CONNECTING_WATCHDOG' }],
  };
}

function handleIdle(
  state: CallPhaseState,
  event: CallPhaseEvent
): CallPhaseResult {
  if (event.type === 'START_TAPPED') {
    return enterConnecting(event.transport);
  }
  return noop(state);
}

function handleConnecting(
  state: CallPhaseState,
  event: CallPhaseEvent
): CallPhaseResult {
  switch (event.type) {
    case 'CAPTURE_READY':
      if (state.transport === 'ws') {
        return {
          state: { ...state, phase: 'live' },
          effects: [{ type: 'CANCEL_CONNECTING_WATCHDOG' }],
        };
      }
      return noop(state); // rtc: informational only, see file header.

    case 'PC_CONNECTED': {
      if (state.transport !== 'rtc' || state.rtcConnected) return noop(state);
      const next = { ...state, rtcConnected: true };
      return next.rtcTrackAttached
        ? {
            state: { ...next, phase: 'live' },
            effects: [{ type: 'CANCEL_CONNECTING_WATCHDOG' }],
          }
        : noop(next);
    }

    case 'TRACK_ATTACHED': {
      if (state.transport !== 'rtc' || state.rtcTrackAttached)
        return noop(state);
      const next = { ...state, rtcTrackAttached: true };
      return next.rtcConnected
        ? {
            state: { ...next, phase: 'live' },
            effects: [{ type: 'CANCEL_CONNECTING_WATCHDOG' }],
          }
        : noop(next);
    }

    case 'ANSWER_RECEIVED':
      return noop(state); // bookkeeping only — see the event's doc comment.

    case 'START_FAILED':
      return {
        state: { ...state, phase: 'failed', reason: event.reason },
        effects: [{ type: 'CANCEL_CONNECTING_WATCHDOG' }],
      };

    case 'END_TAPPED':
      return {
        state: { ...state, phase: 'ending' },
        effects: [{ type: 'CANCEL_CONNECTING_WATCHDOG' }],
      };

    default:
      return noop(state);
  }
}

function handleLive(
  state: CallPhaseState,
  event: CallPhaseEvent
): CallPhaseResult {
  if (event.type === 'END_TAPPED') {
    return { state: { ...state, phase: 'ending' }, effects: [] };
  }
  return noop(state);
}

function handleEnding(
  state: CallPhaseState,
  event: CallPhaseEvent
): CallPhaseResult {
  if (event.type === 'TEARDOWN_DONE') {
    return { state: initialCallPhaseState(), effects: [] };
  }
  return noop(state);
}

function handleFailed(
  state: CallPhaseState,
  event: CallPhaseEvent
): CallPhaseResult {
  if (event.type === 'RETRY_TAPPED') {
    // Reuses `state.transport` — RETRY_TAPPED carries no payload of its own;
    // the driver reads the transport back off the post-dispatch state to
    // know which attempt (ws vs rtc) to actually (re)run.
    return enterConnecting(state.transport ?? 'rtc');
  }
  if (event.type === 'END_TAPPED') {
    return { state: { ...state, phase: 'ending' }, effects: [] };
  }
  return noop(state);
}

export function callPhaseReducer(
  state: CallPhaseState,
  event: CallPhaseEvent
): CallPhaseResult {
  switch (state.phase) {
    case 'idle':
      return handleIdle(state, event);
    case 'connecting':
      return handleConnecting(state, event);
    case 'live':
      return handleLive(state, event);
    case 'ending':
      return handleEnding(state, event);
    case 'failed':
      return handleFailed(state, event);
  }
}
